import { useEffect, useState } from "react";
import { FileText, ImageIcon, FileSpreadsheet, X, Loader2, Paperclip } from "lucide-react";

const isImage = (ct) => (ct || "").startsWith("image/");
const iconFor = (ct) => {
  if (isImage(ct)) return ImageIcon;
  if ((ct || "").includes("spreadsheet") || (ct || "").includes("excel") || (ct || "").includes("csv")) return FileSpreadsheet;
  return FileText;
};

// Renders a shift note's attachments. `items` = [{file_name, content_type}],
// `load(idx, preview)` returns a Promise<Blob>. Image items show thumbnails, others show
// a file chip. Clicking opens the file. `onRemove(idx)` shows a remove control.
export function ShiftNoteAttachments({ items, load, onRemove, dark }) {
  const [urls, setUrls] = useState({}); // idx -> objectURL (images only)

  useEffect(() => {
    let alive = true;
    const created = [];
    (async () => {
      for (let i = 0; i < (items || []).length; i++) {
        if (!isImage(items[i]?.content_type)) continue;
        try {
          const blob = await load(i, true);   // thumbnail: logged as a view
          if (!alive) return;
          const u = URL.createObjectURL(blob);
          created.push(u);
          setUrls((m) => ({ ...m, [i]: u }));
        } catch { /* skip */ }
      }
    })();
    return () => { alive = false; created.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const openFile = async (i) => {
    try {
      const blob = await load(i);
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank");
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch { /* noop */ }
  };

  if (!items || items.length === 0) return null;

  const chipBase = dark
    ? "border-white/15 bg-white/5 text-white hover:bg-white/10"
    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";

  return (
    <div className="flex flex-wrap gap-2" data-testid="note-attachments">
      {items.map((a, i) => {
        const img = isImage(a.content_type);
        const Icon = iconFor(a.content_type);
        return (
          <div key={i} className="relative group" data-testid={`note-attachment-${i}`}>
            {img ? (
              <button type="button" onClick={() => openFile(i)}
                className={`h-16 w-16 rounded-md overflow-hidden border flex items-center justify-center ${dark ? "border-white/15 bg-black/20" : "border-slate-200 bg-slate-100"}`}
                title={a.file_name}>
                {urls[i]
                  ? <img src={urls[i]} alt={a.file_name} className="h-full w-full object-cover" />
                  : <Loader2 className={`h-4 w-4 animate-spin ${dark ? "text-white/60" : "text-slate-400"}`} />}
              </button>
            ) : (
              <button type="button" onClick={() => openFile(i)}
                className={`h-16 min-w-[120px] max-w-[180px] rounded-md border px-2.5 flex flex-col items-start justify-center gap-1 transition-colors ${chipBase}`}
                title={a.file_name}>
                <Icon className="h-4 w-4 shrink-0" />
                <span className="text-[10px] leading-tight truncate w-full text-left">{a.file_name}</span>
              </button>
            )}
            {onRemove && (
              <button type="button" onClick={() => onRemove(i)} data-testid={`note-attachment-remove-${i}`}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-600 text-white flex items-center justify-center shadow hover:bg-rose-700">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const AttachmentIcon = Paperclip;
